import * as Cesium from "cesium";
import { Cartesian2 } from "cesium";
import { getDefaultRoadSettings, } from "../settings/earthbrain-site-settings";
import { FlatGroundOperation, } from "../site/flatground/earthbrain-flatground";
import { AIRoad } from "../site/road/ai-road";
import { AddPointAIRoadCommand, UpdatePointAIRoadCommand, } from "../site/road/ai-road-commands";
import { DesignTileSet } from "../site/tileset/design";
import { PointCloudTileSet } from "../site/tileset/point-cloud";
import { AssetType } from "../types";
import { Activity, ActivityLogger } from "../utilities/activity-logger";
import { CesiumPicker } from "../utilities/cesium-picker";
import { Preview } from "../utilities/earthbrain-preview";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { TileAssetResource } from "./TileAssetResource";
import { ViewerCamera } from "./viewer-camera";
export var EntityName;
(function (EntityName) {
    EntityName["Point_Cloud"] = "pointcloud";
    EntityName["Road_Center_Line"] = "roadcenterline";
    EntityName["Flat_Ground_Edge"] = "flatgroundedge";
    EntityName["Flat_Ground_Edit_Outline"] = "flatgroundeditoutline";
})(EntityName || (EntityName = {}));
export class CesiumView {
    _site;
    _viewer;
    _handler;
    _preview;
    _previewPointPos = undefined;
    _pointSelectionOverlayUpdateCallback;
    _previewAIRoad;
    _aiRoadPreviewPromiseResolver;
    _commandManager;
    // private _input = document.createElement("input");
    _checkFlatGroundUnderCreation;
    _isMouseRightClicked;
    _isMouseMovedAfterRightClicked;
    // This is used to check for new edit for new flat ground is called or not
    // if called then used to clear command manager for new edit.
    _cesiumPicker;
    _options;
    get viewer() {
        return this._viewer;
    }
    get viewerCamera() {
        return ViewerCamera.getInstance(this._viewer);
    }
    get picker() {
        return this._cesiumPicker;
    }
    get commandManager() {
        return this._commandManager;
    }
    get handler() {
        return this._handler;
    }
    get preview() {
        return this._preview;
    }
    constructor(container, token, // Cesium Ion access token
    arcgisToken, // Access token for ArcGIS services
    options, site, commandManager) {
        this._site = site;
        this._commandManager = commandManager;
        // Your access token can be found at: https://cesium.com/ion/tokens.
        // This is the default access token
        Cesium.Ion.defaultAccessToken = token;
        Cesium.ArcGisMapService.defaultAccessToken = arcgisToken;
        // Override Cesium's default ArcGIS URLs (ibasemaps-api.arcgis.com is currently unavailable)
        Cesium.ArcGisMapService.defaultWorldImageryServer = new Cesium.Resource({
            url: "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer",
        });
        Cesium.ArcGisMapService.defaultWorldHillshadeServer = new Cesium.Resource({
            url: "https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer",
        });
        Cesium.ArcGisMapService.defaultWorldOceanServer = new Cesium.Resource({
            url: "https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer",
        });
        // Initialize the Cesium Viewer in the HTML element with the cesiumContainer ID.
        try {
            this._viewer = new Cesium.Viewer(container, {
                terrain: Cesium.Terrain.fromWorldTerrain(),
                selectionIndicator: false,
                infoBox: false,
                animation: false,
                baseLayerPicker: true,
                homeButton: false,
                geocoder: false,
                sceneModePicker: options.enableSceneModePicker,
                timeline: false,
                navigationHelpButton: false,
                fullscreenButton: false,
                requestRenderMode: true,
            });
            // Set ArcGIS World Imagery as default basemap and reflect in BaseLayerPicker
            const viewModel = this._viewer.baseLayerPicker.viewModel;
            const arcgisImagery = viewModel.imageryProviderViewModels.find((vm) => vm.name === "ArcGIS World Imagery");
            if (arcgisImagery) {
                viewModel.selectedImagery = arcgisImagery;
            }
            this._viewer.scene.renderError.addEventListener((e) => {
                console.error("render error", e);
                this._viewer.useDefaultRenderLoop = true;
            });
            Cesium.CesiumWidget.prototype.showErrorPanel = (title, message, error) => {
                console.error("Custom error panel:", title, message, error);
                this._viewer.useDefaultRenderLoop = true; // it prevents from stopping the render loop
            };
        }
        catch (error) {
            console.error(error);
        }
        window.addEventListener("resize", () => {
            setTimeout(() => this._viewer.scene.requestRender(), 500);
        });
        this._viewer.scene.globe.depthTestAgainstTerrain = false;
        if (!this._viewer.scene.pickPositionSupported) {
            window.alert("This browser does not support pickPosition.");
        }
        const defaultRoadSettings = getDefaultRoadSettings();
        //Initialize the pathmap
        // this._pathMap = new Map<string, Road>();
        this._preview = new Preview(this._viewer, defaultRoadSettings);
        this._preview.setSphereVisibility(false);
        this._handler = new Cesium.ScreenSpaceEventHandler(this._viewer.scene.canvas);
        //Removing default left double click handler
        this._viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        // this._input.id = "input";
        // this._input.setAttribute("type", "text");
        // this._input.setAttribute("placeholder", "Z: 0.00 m");
        // this._input.autofocus = true;
        // this._input.style.backgroundColor = "White";
        // this._input.style.position = "absolute";
        // this._input.hidden = true;
        // document.body.appendChild(this._input);
        this._checkFlatGroundUnderCreation = false;
        this._isMouseRightClicked = false;
        this._isMouseMovedAfterRightClicked = false;
        this._previewAIRoad = undefined;
        this._aiRoadPreviewPromiseResolver = undefined;
        // this.makeTerrainTransparent(true, 80, 0.1, 150, 1);
        this._options = Object.assign({}, options);
        this._cesiumPicker = new CesiumPicker(this._viewer, this._preview, this._handler);
    }
    createIOptions(pointCloudId, drawing2dImageId) {
        return {
            site: this._site,
            associatePointCloud: this._site.getPointCloud(pointCloudId),
            associateDrawing2dImage: this._site.getReferenceTopology(drawing2dImageId),
            viewer: this._viewer,
            enableDesignOutlines: this._options.enableDesignDataOutline,
            structureManager: this._site.structureManager,
        };
    }
    _mosueMoveDebounce = undefined;
    _debunceHandlerForSphereMovement(road, flatGroundUnderConstrction, movement) {
        if (this._mosueMoveDebounce) {
            clearTimeout(this._mosueMoveDebounce);
            this._mosueMoveDebounce = undefined;
        }
        this._mosueMoveDebounce = setTimeout(() => {
            this._functionForPreviewSphereMovement(road, flatGroundUnderConstrction, movement);
            this._mosueMoveDebounce = undefined;
        }, 100);
    }
    _functionForPreviewSphereMovement(road, flatGroundUnderConstrction, movement) {
        if (this._clickInProgress) {
            return;
        }
        this._isMouseMovedAfterRightClicked = this._isMouseRightClicked
            ? true
            : false;
        const cartesian = this._cesiumPicker.getPointOnMousePos(movement.endPosition);
        if (cartesian) {
            this._previewPointPos = cartesian.clone();
            EarthBrainUtils.copyCartesian(cartesian, this._previewPointPos);
            this._preview.setSpherePosition(this._previewPointPos);
            // add point to the road id the ditance between it and last point is less than 10 KM
            if (road && road.controlPointLength > 0) {
                const lastAddedPoint = road.cesiumRenderableAlignment.ipLinePoints[road.cesiumRenderableAlignment.ipLinePoints.length - 1];
                const distance = Cartesian2.distance(cartesian, lastAddedPoint.wgs84Point);
                if (distance * 0.001 < 10) {
                    // comparing in KMs
                    this._preview.setLineVisibility(true);
                    this._preview.setLinePosition([lastAddedPoint.wgs84Point, cartesian]);
                }
                else {
                    this._preview.setLinePosition([]);
                }
            }
            else {
                this._preview.setLinePosition([]);
            }
            if (flatGroundUnderConstrction &&
                flatGroundUnderConstrction.GroundPointsCount > 1) {
                this._preview.updateFlatGroundPreviewEntity(flatGroundUnderConstrction, cartesian, FlatGroundOperation.CREATION);
            }
            this._viewer.scene.requestRender();
        }
        else {
            this._previewPointPos = undefined;
        }
    }
    /**
     * Add the tiles in the scene. Could be point cloud, land xml etc.
     * @param id ID of the tileset
     * @param vertexBuffer Point list. Is send for all files.
     * @param indexBuffer index list send when data is available at the server end. For PC calculated internally
     */
    async addTileset(id, type, tilesetInputData, tileAssetOption) {
        //Initialize the tileset
        const tileSet = tileAssetOption
            ? await Cesium.Cesium3DTileset.fromUrl(await TileAssetResource.fromAssetId(id, tileAssetOption.version, tileAssetOption.accessToken, tileAssetOption.tileAssetUrl, type))
            : await Cesium.Cesium3DTileset.fromUrl(await Cesium.IonResource.fromAssetId(id));
        if (type === AssetType.Point_Cloud) {
            const pointCloud = new PointCloudTileSet(id, tileSet, this._viewer, tilesetInputData);
            const transparency = tilesetInputData.styleOptions?.common.transparency;
            if (transparency != undefined) {
                pointCloud.updateTransparency(transparency);
            }
            this._site.addPointCloud(pointCloud);
            ActivityLogger.log(Activity.POINTCLOUD_LOADED);
        }
        else if (type === AssetType.Design_Data) {
            this._site.addDesignData(new DesignTileSet(id, tileSet, this._viewer, tilesetInputData));
            ActivityLogger.log(Activity.DESIGNDATA_LOADED);
        }
        return tileSet;
    }
    _clickInProgress = false;
    enableDraggingForAIRoadPreview() {
        if (!this._previewAIRoad) {
            return;
        }
        this._handler.setInputAction(this._debunceHandlerForSphereMovement.bind(this, undefined, undefined), Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        let pickedCtrlPointID = undefined;
        this._handler.setInputAction(() => {
            this._preview.setSphereVisibility(false);
            if (pickedCtrlPointID &&
                this._previewAIRoad.hasControlPoint(pickedCtrlPointID)) {
                this._commandManager.action(new UpdatePointAIRoadCommand(this._previewAIRoad, pickedCtrlPointID, this._previewPointPos.clone(), this._updatePointSelectionOverlay.bind(this)), this);
                this._viewer.scene.screenSpaceCameraController.enableRotate = true;
            }
        }, Cesium.ScreenSpaceEventType.LEFT_UP);
        this._handler.setInputAction((click) => {
            const pickModel = this._viewer.scene.pick(click.position);
            if (pickModel && pickModel.id && pickModel.id.name === "sphereEntity") {
                pickedCtrlPointID = pickModel.id.id;
                if (this._previewAIRoad.hasControlPoint(pickedCtrlPointID)) {
                    this._preview.setSphereVisibility(true);
                    this._viewer.scene.screenSpaceCameraController.enableRotate = false;
                }
            }
            else {
                pickedCtrlPointID = undefined;
            }
        }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
    }
    updateAIRoadPreviewPointAtIndex(pointIndex, position) {
        const pointId = this._previewAIRoad.getControlPointIDAtIndex(pointIndex);
        if (pointId === undefined) {
            return;
        }
        this._commandManager.action(new UpdatePointAIRoadCommand(this._previewAIRoad, pointId, new Cesium.Cartesian3(position.x, position.y, position.z), this._updatePointSelectionOverlay.bind(this)), this);
    }
    _updatePointSelectionOverlay() {
        if (this._pointSelectionOverlayUpdateCallback) {
            const commandInputs = this.createIOptions(this._previewAIRoad.associatePointCloudId);
            // point selection overlay data
            let firsPointData;
            let secondPointData;
            //coordinates and height calculation of point Above point cloud and Design data if they are active
            const calcData = (point) => {
                const pointAtHeightCartoForPointCloud = Cesium.Cartographic.fromCartesian(point);
                const pointAtHeightCartoForDesignData = Cesium.Cartographic.fromCartesian(point);
                pointAtHeightCartoForPointCloud.height =
                    EarthBrainUtils.isPointAboveTheTileset(point, commandInputs.associatePointCloud)
                        ? pointAtHeightCartoForPointCloud.height - 1000
                        : pointAtHeightCartoForPointCloud.height + 1000;
                pointAtHeightCartoForDesignData.height =
                    EarthBrainUtils.isPointAboveTheTileset(point, commandInputs.associatePointCloud)
                        ? pointAtHeightCartoForDesignData.height - 1000
                        : pointAtHeightCartoForDesignData.height + 1000;
                const pointAtHeightForPointCloud = Cesium.Cartographic.toCartesian(pointAtHeightCartoForPointCloud);
                const pointAtHeightForDesignData = Cesium.Cartographic.toCartesian(pointAtHeightCartoForDesignData);
                Cesium.Cartesian3.normalize(Cesium.Cartesian3.subtract(pointAtHeightForPointCloud, point, pointAtHeightForPointCloud), pointAtHeightForPointCloud);
                Cesium.Cartesian3.normalize(Cesium.Cartesian3.subtract(pointAtHeightForDesignData, point, pointAtHeightForDesignData), pointAtHeightForDesignData);
                const rayPointCloud = new Cesium.Ray(point, pointAtHeightForPointCloud);
                const rayDesignData = new Cesium.Ray(point, pointAtHeightForDesignData);
                let pointOnPointCloud = undefined;
                let pointOnPointCloudCarto = undefined;
                if (commandInputs.associatePointCloud) {
                    pointOnPointCloud =
                        commandInputs.associatePointCloud.rayCast(rayPointCloud);
                }
                if (pointOnPointCloud) {
                    pointOnPointCloudCarto =
                        Cesium.Cartographic.fromCartesian(pointOnPointCloud);
                }
                const heightFromPointCloud = pointOnPointCloudCarto
                    ? pointOnPointCloudCarto.height
                    : 0;
                const pointOnDesignData = commandInputs.site.getActiveDesignData()?.rayCast(rayDesignData);
                let pointOnDesignDataCarto = undefined;
                if (pointOnDesignData) {
                    pointOnDesignDataCarto =
                        Cesium.Cartographic.fromCartesian(pointOnDesignData);
                }
                const heightFromDesignData = pointOnDesignDataCarto
                    ? pointOnDesignDataCarto.height
                    : 0;
                if (!commandInputs.associatePointCloud) {
                    console.log("NO ACTIVE POINT CLOUD DATA");
                }
                if (!commandInputs.site.getActiveDesignData()) {
                    console.log("NO ACTIVE DESIGN DATA");
                }
                return {
                    x: point.x,
                    y: point.y,
                    z: point.z,
                    heightFromPointCloud,
                    heightFromDesignData,
                };
            };
            try {
                firsPointData = calcData(this._previewAIRoad.startPoint.clone());
                secondPointData = calcData(this._previewAIRoad.endPoint.clone());
            }
            catch (e) {
                console.log(e);
            }
            this._pointSelectionOverlayUpdateCallback([
                firsPointData,
                secondPointData,
            ]);
            return [firsPointData, secondPointData];
        }
    }
    setAIRoadPreviewCallback(callback) {
        this._pointSelectionOverlayUpdateCallback = callback;
    }
    _finishAIRoadDragging() {
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP);
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN);
        this._preview.setSphereVisibility(false);
    }
    async deleteAIRoadPreview() {
        if (!this._previewAIRoad) {
            return;
        }
        this._finishAIRoadDragging();
        if (this._previewAIRoad.getcontrolPointCount() === 2) {
            const aiRoadPreviewData = this._updatePointSelectionOverlay();
            const convertedCords = await this._pointSelectionOverlayUpdateCallback(aiRoadPreviewData);
            const promiseData = [
                {
                    CesiumCRS: {
                        x: aiRoadPreviewData[0].x,
                        y: aiRoadPreviewData[0].y,
                        z: aiRoadPreviewData[0].z,
                    },
                    PointCLoudCRS: {
                        x: convertedCords[0].x,
                        y: convertedCords[0].y,
                        z: convertedCords[0].z,
                    },
                    heightFromPointCloud: aiRoadPreviewData[0].heightFromPointCloud,
                    heightFromDesignData: aiRoadPreviewData[0].heightFromDesignData,
                },
                {
                    CesiumCRS: {
                        x: aiRoadPreviewData[1].x,
                        y: aiRoadPreviewData[1].y,
                        z: aiRoadPreviewData[1].z,
                    },
                    PointCLoudCRS: {
                        x: convertedCords[1].x,
                        y: convertedCords[1].y,
                        z: convertedCords[1].z,
                    },
                    heightFromPointCloud: aiRoadPreviewData[1].heightFromPointCloud,
                    heightFromDesignData: aiRoadPreviewData[1].heightFromDesignData,
                },
            ];
            this._aiRoadPreviewPromiseResolver &&
                this._aiRoadPreviewPromiseResolver(promiseData);
            this._aiRoadPreviewPromiseResolver = undefined;
            this._pointSelectionOverlayUpdateCallback = undefined;
        }
        this._previewAIRoad.remove(this._viewer);
        this._previewAIRoad = undefined;
        this._commandManager.clear();
        // this._viewer.scene.requestRender();
    }
    startAIRoadPreviewCreation() {
        if (this._previewAIRoad) {
            this._finishAIRoadDragging();
            this._previewAIRoad.remove(this._viewer);
            this._previewAIRoad = undefined;
            this._commandManager.clear();
        }
        this._preview.setSphereVisibility(true);
        this._handler.setInputAction(this._debunceHandlerForSphereMovement.bind(this, undefined, undefined), Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        let pointCount = 0;
        const road = this._createAIRoadPreview();
        this._handler.setInputAction(() => {
            pointCount++;
            this._commandManager.action(new AddPointAIRoadCommand(road, this._previewPointPos.clone()), this);
            if (pointCount === 2) {
                this._stopAIRoadCreation();
                this._previewAIRoad = road;
                this._updatePointSelectionOverlay();
                this.enableDraggingForAIRoadPreview();
            }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
        return new Promise((resolve) => {
            this._aiRoadPreviewPromiseResolver = resolve;
        });
    }
    _stopAIRoadCreation() {
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        this._preview.setSphereVisibility(false);
    }
    _createAIRoadPreview() {
        this._previewAIRoad = new AIRoad(this._viewer);
        // this._viewer.scene.requestRender();
        return this._previewAIRoad;
    }
    _onMouseRightDown() {
        this._isMouseRightClicked = true;
        this._handler.setInputAction(this._onMouseRightUp.bind(this), Cesium.ScreenSpaceEventType.RIGHT_UP);
    }
    _onMouseRightUp() {
        this._isMouseRightClicked = false;
        this._handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_UP);
    }
    isMouseDraggedAfterRightClick() {
        if (this._isMouseMovedAfterRightClicked) {
            this._isMouseMovedAfterRightClicked = false;
            return true;
        }
        else {
            return false;
        }
    }
    raiseEventInvalidCtrlPtForRoad(roadID) {
        const event = new CustomEvent("invalidControlPointForRoad", {
            detail: { roadID, type: 0 },
        });
        window.dispatchEvent(event);
    }
    fitToFlatGround(flatGroundId) {
        const flatGround = this._site.getFlatGround(flatGroundId);
        flatGround && flatGround.fitToFlatGround();
        // this._viewer.scene.requestRender();
    }
    async setFlatGroundPointsUniformHeight(id, height) {
        const flatGround = this._site.getFlatGround(id);
        flatGround.setUniformPointsHeight(height);
        flatGround.updateSpheresPositionAndLabel();
        await flatGround.computeGeometry();
    }
    checkFlatGroundUnderCreation() {
        return this._checkFlatGroundUnderCreation;
    }
    _disableFlatGroundHandler;
    disableFlatGroundCreationHandler() {
        this._disableFlatGroundHandler();
    }
    _cancelFlatGroundCreationHandler;
    cancelFlatGroundCreation() {
        this._checkFlatGroundUnderCreation &&
            this._cancelFlatGroundCreationHandler &&
            this._cancelFlatGroundCreationHandler();
        this._checkFlatGroundUnderCreation = false;
        this._cancelFlatGroundCreationHandler = undefined;
        // this._viewer.scene.requestRender();
    }
    toggleFlatGroundVisibility(flatGroundID, show) {
        const flatGroundUnderEdit = this._site.getFlatGround(flatGroundID);
        if (!flatGroundUnderEdit) {
            return;
        }
        flatGroundUnderEdit.toggleFlatGroundVisibility(show);
        // this._viewer.scene.requestRender();
    }
    async updateFlatGroundMaterial(flatGroundID, commonMaterial, embankmentMaterial, cutMaterial) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (!flatGround) {
            return;
        }
        await flatGround.updateMaterial(commonMaterial, embankmentMaterial, cutMaterial);
        // this._viewer.scene.requestRender();
    }
    getRoadHeightInfo(roadID) {
        const road = this._site.getRoad(roadID);
        if (!road) {
            return undefined;
        }
        const heightInfo = road.getHeightInfo();
        return heightInfo;
    }
    getRoadSlopeInfo(roadID) {
        const road = this._site.getRoad(roadID);
        return road ? road.getSlopeInfo() : undefined;
    }
    getMeshGeometryOfFlat(flatGroundID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        if (flatGround) {
            const geometry = flatGround.getGeometry();
            const uniqueGeometry = EarthBrainUtils.removeDuplicateVertices(geometry);
            if (uniqueGeometry.vertices.length > 0) {
                EarthBrainUtils.removeInvalidFacesFromGeometry(uniqueGeometry);
                return {
                    vertices: uniqueGeometry.vertices,
                    indices: uniqueGeometry.indices,
                };
            }
        }
        return undefined;
    }
    getFlatGroundInfo(flatGroundId) {
        const flatGround = this._site.getFlatGround(flatGroundId);
        return flatGround ? flatGround.toJSON() : undefined;
    }
    getCtrlPointCordiOnFlatGround(flatGroundID, flatGroundPointID) {
        const flatGround = this._site.getFlatGround(flatGroundID);
        return flatGround?.getControlPointPos(flatGroundPointID);
    }
    makeTerrainTransparent(status, near = 200, nearTransparancyValue = 0.3, far = 400, farTransparancyValue = 1) {
        if (status) {
            this._viewer.scene.globe.translucency.enabled = status;
            this._viewer.scene.screenSpaceCameraController.enableCollisionDetection = false;
            this._viewer.scene.globe.translucency.frontFaceAlphaByDistance =
                new Cesium.NearFarScalar(near, nearTransparancyValue, far, farTransparancyValue);
        }
        else {
            this._viewer.scene.globe.translucency.enabled = status;
            this._viewer.scene.screenSpaceCameraController.enableCollisionDetection =
                status;
        }
        // this._viewer.scene.requestRender();
    }
    toggleGlobeVisibility(visibility) {
        this._viewer.scene.globe.show = visibility;
        // this._viewer.scene.requestRender();
    }
    clear() {
        // this._sectionPlane.show = false;
        this._site.structureManager.clear();
        this._site.disposeAll();
        // this._viewer.scene.requestRender();
    }
    pickNumberOfPoints(numberOfPointsToPick, selector) {
        return this._cesiumPicker.pickNumberOfPoints(numberOfPointsToPick, selector);
    }
    cancelPicking(msg) {
        this._cesiumPicker.abortPickPoint(msg);
    }
    // public getDMSCoordinates(pageX: number, pageY: number): string[] | undefined {
    //   return EarthBrainUtils.getDMSCoordinates(this._viewer, pageX, pageY);
    // }
    getXYZCoordinates(offsetX, offsetY) {
        const coord = this._cesiumPicker.getPointOnMousePos(new Cesium.Cartesian2(offsetX, offsetY));
        if (coord) {
            return [coord.x, coord.y, coord.z];
        }
    }
    requestRender() {
        setTimeout(() => {
            this._viewer.scene.requestRender();
        }, 300);
    }
    createSphere(pos, color) {
        return EarthBrainUtils.addSphere(this._viewer, pos, Cesium.Color.fromCssColorString(color)).id;
    }
    createRay(pos, size) {
        return EarthBrainUtils.renderRay(pos, this._viewer, size);
    }
    createIGeometry(geom, color) {
        const primitive = EarthBrainUtils.createEntityFromIndexedGeometry(geom, Cesium.Color.fromCssColorString(color));
        this._viewer.scene.primitives.add(primitive);
        return primitive;
    }
    getControlPointIndexById(roadId, pointId) {
        const road = this._site.getRoad(roadId);
        return road ? road.getControlPointIndex(pointId) : -1;
    }
    /**
     * Recalculates geometry
     * @param roadId
     */
    recalculateRoadGeometry(roadId) {
        const road = this._site.getRoad(roadId);
        road && road.updateRoadGeometry();
    }
    updateRoadAndFlatLabelColors(color) {
        this._site.getRoads().forEach((p) => p.updateLabelColors(color));
        this._site.getFlatGrounds().forEach((p) => p.updateLabelColors(color));
        // this._viewer.scene.requestRender();
    }
}
//# sourceMappingURL=view-3d-cesium.js.map